/*
 * Aurika wishlist — client-side, localStorage-backed (key: aurika:wishlist).
 * No app dependency. Loaded deferred from the header section.
 *
 * Wired markup:
 *  - .aurika-wish buttons (snippets/wishlist-toggle.liquid) — toggle save state
 *  - [data-wishlist-open] header icon, [data-wishlist-count] badge
 *  - #aurika-wishlist-drawer / #aurika-wishlist-items (snippets/wishlist-drawer.liquid)
 *  - Add-to-cart mirrors the theme's product-form flow (cart:prepare-bundled-sections →
 *    /cart/add.js with sections → cart:change with baseEvent "variant:add") so the native
 *    cart drawer opens and re-renders itself.
 */
(function () {
  'use strict';

  var KEY = 'aurika:wishlist';

  function read() {
    try {
      var raw = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(raw) ? raw : [];
    } catch (e) {
      return [];
    }
  }

  function write(items) {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (e) {
      /* storage full/blocked — state still lives in memory for this page */
    }
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { count: items.length } }));
  }

  function has(handle) {
    return read().some(function (i) { return i.handle === handle; });
  }

  function toggle(item) {
    var items = read();
    var idx = items.findIndex(function (i) { return i.handle === item.handle; });
    if (idx > -1) {
      items.splice(idx, 1);
    } else {
      items.push(item);
    }
    write(items);
  }

  function remove(handle) {
    write(read().filter(function (i) { return i.handle !== handle; }));
  }

  /* ── UI sync ─────────────────────────────────────────────────────────── */

  function syncButtons() {
    var buttons = document.querySelectorAll('.aurika-wish');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var active = has(btn.getAttribute('data-wish-handle'));
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      var title = btn.getAttribute('data-wish-title') || '';
      btn.setAttribute('aria-label', (active ? 'Remove ' : 'Save ') + title + (active ? ' from wishlist' : ' to wishlist'));
      var label = btn.querySelector('[data-wish-label]');
      if (label) label.textContent = active ? 'Saved to wishlist' : 'Save to wishlist';
    }
  }

  function syncCount() {
    var count = read().length;
    var badges = document.querySelectorAll('[data-wishlist-count]');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = count > 9 ? '9+' : String(count);
      badges[i].classList.toggle('is-visible', count > 0);
    }
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function renderDrawer() {
    var list = document.getElementById('aurika-wishlist-items');
    if (!list) return;
    list.textContent = '';

    var items = read();
    if (!items.length) {
      var tpl = document.getElementById('aurika-wishlist-empty');
      if (tpl) list.appendChild(tpl.content.cloneNode(true));
      return;
    }

    items.forEach(function (item) {
      var row = el('div', 'aurika-wishlist-item');

      var link = el('a', 'aurika-wishlist-item__media');
      link.href = item.url || '#';
      if (item.image) {
        var img = document.createElement('img');
        img.src = item.image;
        img.alt = item.title || '';
        img.loading = 'lazy';
        img.width = 64;
        img.height = 80;
        link.appendChild(img);
      }
      row.appendChild(link);

      var info = el('div', 'aurika-wishlist-item__info');
      var titleLink = el('a', 'aurika-wishlist-item__title', item.title || '');
      titleLink.href = item.url || '#';
      info.appendChild(titleLink);

      var priceRow = el('p', 'aurika-wishlist-item__price');
      priceRow.appendChild(el('span', 'aurika-wishlist-item__price-current', item.price || ''));
      if (item.comparePrice) {
        priceRow.appendChild(el('span', 'aurika-wishlist-item__price-compare', item.comparePrice));
      }
      info.appendChild(priceRow);

      var actions = el('div', 'aurika-wishlist-item__actions');
      if (item.variant) {
        var atc = el('button', 'aurika-wishlist-item__atc', 'Add to cart');
        atc.type = 'button';
        atc.setAttribute('data-wishlist-atc', item.variant);
        actions.appendChild(atc);
      }
      var rm = el('button', 'aurika-wishlist-item__remove', 'Remove');
      rm.type = 'button';
      rm.setAttribute('data-wishlist-remove', item.handle);
      rm.setAttribute('aria-label', 'Remove ' + (item.title || 'item') + ' from wishlist');
      actions.appendChild(rm);
      info.appendChild(actions);

      row.appendChild(info);
      list.appendChild(row);
    });
  }

  /* ── Drawer open/close ───────────────────────────────────────────────── */

  var lastFocused = null;

  function openDrawer() {
    var drawer = document.getElementById('aurika-wishlist-drawer');
    if (!drawer) return;
    renderDrawer();
    lastFocused = document.activeElement;
    drawer.hidden = false;
    requestAnimationFrame(function () {
      drawer.classList.add('is-open');
      var close = drawer.querySelector('.aurika-wishlist-drawer__close');
      if (close) close.focus();
    });
  }

  function closeDrawer() {
    var drawer = document.getElementById('aurika-wishlist-drawer');
    if (!drawer || drawer.hidden) return;
    drawer.classList.remove('is-open');
    window.setTimeout(function () { drawer.hidden = true; }, 220);
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  /* ── Add to cart (mirrors theme product-form flow) ───────────────────── */

  function addToCart(button) {
    var variantId = parseInt(button.getAttribute('data-wishlist-atc'), 10);
    if (!variantId) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    var bundle = new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: [] } });
    document.documentElement.dispatchEvent(bundle);

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: bundle.detail.sections.join(',') })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('add failed');
        return response.json();
      })
      .then(function (responseJson) {
        return fetch(root + 'cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cartContent) {
            cartContent['sections'] = responseJson['sections'];
            closeDrawer();
            document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
              bubbles: true,
              detail: { baseEvent: 'variant:add', cart: cartContent }
            }));
          });
      })
      .catch(function () {
        button.textContent = 'Unavailable';
      })
      .finally(function () {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      });
  }

  /* ── Events ──────────────────────────────────────────────────────────── */

  document.addEventListener('click', function (event) {
    var wish = event.target.closest('.aurika-wish');
    if (wish) {
      event.preventDefault();
      event.stopPropagation();
      toggle({
        handle: wish.getAttribute('data-wish-handle'),
        title: wish.getAttribute('data-wish-title'),
        url: wish.getAttribute('data-wish-url'),
        price: wish.getAttribute('data-wish-price'),
        comparePrice: wish.getAttribute('data-wish-compare-price'),
        image: wish.getAttribute('data-wish-image'),
        variant: wish.getAttribute('data-wish-variant')
      });
      return;
    }

    if (event.target.closest('[data-wishlist-open]')) {
      event.preventDefault();
      openDrawer();
      return;
    }

    if (event.target.closest('[data-wishlist-close]')) {
      closeDrawer();
      return;
    }

    var rm = event.target.closest('[data-wishlist-remove]');
    if (rm) {
      remove(rm.getAttribute('data-wishlist-remove'));
      return;
    }

    var atc = event.target.closest('[data-wishlist-atc]');
    if (atc) {
      addToCart(atc);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeDrawer();
  });

  document.addEventListener('wishlist:change', function () {
    syncButtons();
    syncCount();
    renderDrawer();
  });

  /* Cross-tab sync */
  window.addEventListener('storage', function (event) {
    if (event.key === KEY) {
      syncButtons();
      syncCount();
      renderDrawer();
    }
  });

  /* Cards re-rendered by filters/carousels: re-sync when new hearts appear */
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType === 1 && (node.matches && node.matches('.aurika-wish') || node.querySelector && node.querySelector('.aurika-wish'))) {
          syncButtons();
          return;
        }
      }
    }
  });

  function init() {
    /* The drawer is rendered inside the header section; re-parent it to <body>
       so position:fixed can't be broken by sticky/transformed header ancestors. */
    var drawer = document.getElementById('aurika-wishlist-drawer');
    if (drawer && drawer.parentElement !== document.body) {
      document.body.appendChild(drawer);
    }
    syncButtons();
    syncCount();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
